import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Trash2, RefreshCw, Check, Settings } from 'lucide-react';
import { candidatesAPI } from "../../services/api";
import { toast } from "react-toastify";

const FixCandidateFieldsModal = ({ onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [configExists, setConfigExists] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const contentRef = useRef(null);
  const modalRef = useRef(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Get organization ID from localStorage and initialize
  useEffect(() => {
    const getOrgId = () => {
      try {
        const profile = JSON.parse(localStorage.getItem("userProfile"));
        const orgId = profile?.organizationId || profile?.OrganizationId || localStorage.getItem("organizationId");
        
        if (!orgId) {
          toast.error("Organization ID not found. Please login again.");
          onClose();
          return null;
        }
        return orgId;
      } catch (e) {
        console.error("Error getting organization ID:", e);
        toast.error("Organization ID not found. Please login again.");
        onClose();
        return null;
      }
    };

    const orgId = getOrgId();
    if (orgId) {
      setOrganizationId(orgId);
      initializeModal();
    }
  }, [onClose]);

  // Initialize modal
  const initializeModal = async () => {
    setInitializing(true);
    try {
      // Check if configuration exists
      const existsResponse = await candidatesAPI.checkConfigExists();
      const exists = existsResponse.data === true || existsResponse.data?.exists === true;
      setConfigExists(exists);

      // Fetch available fields
      await fetchAvailableFields();

      // If config exists, fetch configured fields
      if (exists) {
        await fetchConfiguredFields();
      }
    } catch (error) {
      console.error('Error initializing modal:', error);
      toast.error('Failed to load field configuration');
    } finally {
      setInitializing(false);
    }
  };

  // Fetch all available fields
  const fetchAvailableFields = async () => {
    try {
      const response = await candidatesAPI.getAvailableFields();
      const fields = Array.isArray(response.data) ? response.data : [];
      setAvailableFields(fields);
      
      // If no config exists and no fields selected yet, select all fields
      if (!configExists && selectedFields.length === 0 && fields.length > 0) {
        setSelectedFields(fields);
      }
    } catch (error) {
      console.error('Error fetching available fields:', error);
      toast.error('Failed to fetch available fields');
    }
  };

  // Fetch configured fields if config exists
  const fetchConfiguredFields = async () => {
    try {
      const response = await candidatesAPI.getFields();
      const fields = Array.isArray(response.data) ? response.data : [];
      setSelectedFields(fields);
    } catch (error) {
      console.error('Error fetching configured fields:', error);
      toast.error('Failed to fetch configured fields');
    }
  };

  // Handle field selection toggle
  const handleFieldToggle = (field) => {
    setSelectedFields(prev => {
      const isSelected = prev.includes(field);
      if (isSelected) {
        return prev.filter(f => f !== field);
      } else {
        return [...prev, field];
      }
    });
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedFields.length === availableFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...availableFields]);
    }
  };

const handleSave = async () => {
  if (selectedFields.length === 0) {
    toast.warning('Please select at least one field');
    return;
  }

  setLoading(true);
  try {
    // Prepare payload exactly as shown in your example
    const payload = {
      selectedFields: selectedFields
    };

    console.log('Sending field configuration:', {
      endpoint: '/api/field-configs',
      payload: payload,
      selectedFieldsCount: selectedFields.length
    });

    // Call the API
    const response = await candidatesAPI.saveOrUpdateFieldConfig(payload);
    console.log('API Response:', response);
    
    // Check if response indicates success
    if (response.status === 200 || response.status === 201) {
      toast.success(
        configExists 
          ? 'Field configuration updated successfully' 
          : 'Field configuration created successfully'
      );
      
      setConfigExists(true);
      onSave?.();
      onClose();
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
    
  } catch (error) {
    console.error('Error saving field configuration:', error);
    
    if (error.response) {
      const { status, data } = error.response;
      console.error('Error details:', { 
        status, 
        data,
        url: error.config?.url,
        method: error.config?.method 
      });
      
      if (status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        setTimeout(() => window.location.href = '/login', 1500);
      } else if (status === 403) {
        toast.error('Access denied. You need ADMIN or ATS role to configure fields.');
      } else if (status === 400) {
        toast.error(data?.message || 'Bad request. Please check the fields selected.');
      } else if (status === 500) {
        toast.error(data?.message || 'Server error. Please try again later.');
      } else {
        toast.error(data?.message || `Failed to save (${status})`);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      toast.error('Network error. Please check your connection.');
    } else {
      console.error('Request setup error:', error.message);
      toast.error('Failed to save field configuration: ' + error.message);
    }
  } finally {
    setLoading(false);
  }
};
  // Delete configuration
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the field configuration? This will reset all fields to default.')) {
      return;
    }

    setLoading(true);
    try {
      await candidatesAPI.deleteFieldConfig();
      toast.success('Field configuration deleted successfully');
      setConfigExists(false);
      setSelectedFields(availableFields); // Select all fields after deletion
      onSave?.();
    } catch (error) {
      console.error('Error deleting field configuration:', error);
      const message = error.response?.data?.message || 'Failed to delete field configuration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh/Reload configuration
  const handleRefresh = async () => {
    setLoading(true);
    try {
      await initializeModal();
      toast.success('Configuration refreshed');
    } catch (error) {
      console.error('Error refreshing configuration:', error);
      toast.error('Failed to refresh configuration');
    } finally {
      setLoading(false);
    }
  };

  // Format field name for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Group similar fields together
  const getFieldCategory = (field) => {
    const categories = {
      'Basic Info': ['firstName', 'lastName', 'primaryEmail', 'primaryPhone', 'dateOfBirth', 'gender'],
      'Professional': ['currentJobTitle', 'currentCompany', 'totalYearsOfExperience', 'noticePeriod'],
      'Compensation': ['currentCTC', 'expectedCTC', 'salaryCurrency'],
      'Education': ['highestEducationLevel', 'fieldOfStudy', 'universityName', 'gpa'],
      'Location': ['city', 'state', 'country', 'currentLocation', 'preferredLocations'],
      'Skills': ['technicalSkills', 'softSkills', 'languages'],
      'Documents': ['resumeUrl', 'coverLetterUrl', 'profilePhotoUrl'],
      'Contact': ['whatsappNumber', 'linkedInProfile', 'githubProfile', 'portfolioWebsite'],
      'Status': ['status', 'jobId', 'sourceOfApplication', 'createdAt']
    };

    for (const [category, fields] of Object.entries(categories)) {
      if (fields.includes(field)) {
        return category;
      }
    }
    return 'Other';
  };

  // Group fields by category
  const groupedFields = availableFields.reduce((groups, field) => {
    const category = getFieldCategory(field);
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(field);
    return groups;
  }, {});

  // Sort categories for better display
  const sortedCategories = Object.keys(groupedFields).sort();

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
      
      {/* Modal positioned top-right */}
      <div className="fixed inset-0 flex items-start justify-end p-4">
        <div 
          ref={modalRef}
          className="bg-white w-full max-w-md flex flex-col shadow-xl rounded-lg overflow-hidden"
          style={{ 
            height: 'calc(100vh - 2rem)', // Full height minus padding
            maxHeight: 'calc(100vh - 2rem)'
          }}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="text-blue-600" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Fix Candidate Form Fields
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure which fields appear in candidate forms
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Status Bar */}
          <div className={`flex-shrink-0 px-6 py-3 border-b ${configExists ? 'bg-green-50' : 'bg-yellow-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {configExists ? (
                  <>
                    <Check className="text-green-600" size={16} />
                    <span className="text-sm font-medium text-green-900">
                      Configuration exists ({selectedFields.length}/{availableFields.length} fields selected)
                    </span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-yellow-900">
                      No configuration found - Create new one
                    </span>
                  </>
                )}
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white rounded transition-colors"
                title="Refresh configuration"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Select All */}
          <div className="flex-shrink-0 px-6 py-3 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={selectedFields.length === availableFields.length && availableFields.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                <span className="text-sm font-medium text-gray-900">
                  Select All Fields
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  ({selectedFields.length} selected of {availableFields.length})
                </span>
              </label>
            </div>
          </div>

          {/* Fields List - Scrollable */}
          <div 
            ref={contentRef}
            className="flex-1 min-h-0 overflow-y-auto"
          >
            {initializing ? (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-600">Loading fields...</p>
                </div>
              </div>
            ) : availableFields.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center p-8 text-gray-500">
                <Settings className="mb-3" size={32} />
                <p className="text-sm">No fields available</p>
              </div>
            ) : (
              <div className="p-4">
                {sortedCategories.map(category => (
                  <div key={category} className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2 sticky top-0 bg-white py-1 z-10">
                      {category}
                    </h3>
                    <div className="space-y-1">
                      {groupedFields[category].map(field => (
                        <div
                          key={field}
                          className={`flex items-center p-2 rounded-md cursor-pointer transition-all ${
                            selectedFields.includes(field) 
                              ? 'bg-blue-50 border border-blue-100' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleFieldToggle(field)}
                        >
                          <div className={`w-4 h-4 border rounded flex items-center justify-center mr-3 flex-shrink-0 ${
                            selectedFields.includes(field)
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {selectedFields.includes(field) && (
                              <Check size={12} className="text-white" />
                            )}
                          </div>
                          <span className="text-sm text-gray-900 flex-1">
                            {formatFieldName(field)}
                          </span>
                          {['firstName', 'lastName', 'primaryEmail'].includes(field) && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded flex-shrink-0">
                              Required
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex justify-between items-center gap-3 px-6 py-4 border-t bg-gray-50">
            <div className="flex gap-2">
              {configExists && (
                <button
                  onClick={handleDelete}
                  disabled={loading || initializing}
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading || initializing || selectedFields.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Save size={14} />
                {configExists ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixCandidateFieldsModal;